import {  useState } from 'react'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import { PlatformTabs, type Platform } from '@/features/integrations/components/PlatformTabs'
import {   CLI_INSTALL_GITHUB, CLI_GITLAB_SETUP, CLI_GITLAB_ID_TOKENS } from '@/features/integrations/snippets'

// ── Terramantle CLI: Using in CI ─────────────────────────────────────────────
export function CliCiGuide({ org, onSwitchGuide }: { org: OrgItem | null; onSwitchGuide: (id: GuideId) => void }) {
  const [platform, setPlatform] = useState<Platform>('github')
  const orgSlug = org?.slug ?? '<org-slug>'

  const githubSnippet = `name: terramantle

on:
  push:
    branches: [main]

permissions:
  contents: read
  id-token: write          # required: lets terramantle fetch the ambient OIDC token

jobs:
  push-lock:
    runs-on: ubuntu-latest
    env:
      TERRAMANTLE_ORG: ${orgSlug}   # required in CI - no org lookup for OIDC tokens
    steps:
      - uses: actions/checkout@v4
      - uses: opentofu/setup-opentofu@v1

${CLI_INSTALL_GITHUB}

      # Auth mode auto-detects GITHUB_ACTIONS; no secret is configured anywhere.
      - name: Push the provider lock file
        run: |
          tofu init
          terramantle lock push --workspace prod --fail-on-atrisk`

  const gitlabSnippet = `push-lock:
  image: ubuntu:24.04
${CLI_GITLAB_ID_TOKENS}
  variables:
    TERRAMANTLE_ORG: ${orgSlug}   # required in CI
${CLI_GITLAB_SETUP}
  # Auth mode auto-detects GITLAB_CI and uses $TERRAMANTLE_ID_TOKEN as the bearer.
  script:
    - tofu init
    - terramantle lock push --workspace prod --fail-on-atrisk`

  const botSnippet = `# Runner with no OIDC? Fall back to a bot token's client credentials.
# Store both as masked CI secrets; the CLI picks up \`client\` mode on its own.
export TERRAMANTLE_CLIENT_ID=$TM_CLIENT_ID
export TERRAMANTLE_CLIENT_SECRET=$TM_CLIENT_SECRET
export TERRAMANTLE_ORG=${orgSlug}

terramantle lock push --workspace prod --fail-on-atrisk`

  const scriptingSnippet = `# Branch on the posture gate (exit 3) rather than failing the whole job
rc=0
terramantle lock push --workspace prod --fail-on-atrisk || rc=$?
case "$rc" in
  0) echo "posture clean" ;;
  3) echo "::warning::providers at risk - raising a ticket instead of blocking" ;;
  *) exit "$rc" ;;
esac

# Feed another step from machine output
terramantle modules changed --all -o json > changed.json
terramantle providers ls --at-risk -o json | jq -r '.[].name'`

  return (
    <div>
      <h3 style={s.guideTitle}>Using the CLI in CI</h3>
      <p style={s.para}>
        The CLI is built to run non-interactively and to find the ambient OIDC identity by itself. On GitHub
        Actions no static secret is needed at all; GitLab needs an <code style={s.code}>id_tokens:</code>{' '}
        block to mint one. Claim the repository first under Organisation settings →{' '}
        <button style={s.linkBtn} onClick={() => onSwitchGuide('oidc-trust')}>Pipeline Trust</button>, or the
        token will verify but match no trusted subject.
      </p>

      <h3 style={s.subTitle}>Pipeline</h3>
      <PlatformTabs value={platform} onChange={setPlatform} only={['github', 'gitlab']} />
      {platform === 'github' && <ShellSnippet code={githubSnippet} language="yaml" />}
      {platform === 'gitlab' && <ShellSnippet code={gitlabSnippet} language="yaml" />}
      <p style={{ ...s.para, marginTop: 10 }}>
        {platform === 'github'
          ? <>Without <code style={s.code}>id-token: write</code> the runner cannot mint a token and the CLI exits <code style={s.code}>5</code>.</>
          : <>The <code style={s.code}>aud</code> must match the deployment's audience, and the CLI's release binaries are glibc - hence a Debian/Ubuntu image rather than an Alpine-based one.</>}
      </p>

      <h3 style={s.subTitle}>Without OIDC</h3>
      <ShellSnippet code={botSnippet} language="bash" />

      <h3 style={s.subTitle}>Scripting against it</h3>
      <ShellSnippet code={scriptingSnippet} language="bash" />

      <h3 style={s.subTitle}>Notes</h3>
      <ul style={s.list}>
        <li><code style={s.code}>TERRAMANTLE_ORG</code> is <strong>required</strong> in CI. An OIDC or bot token
          is server-resolved from repo trust, so there is no org endpoint for the CLI to fall back on.</li>
        <li>Pass <code style={s.code}>--ci</code> (publish) or <code style={s.code}>--yes</code> (the
          confirming verbs) so nothing waits on a prompt; without them a non-interactive run exits{' '}
          <code style={s.code}>4</code>.</li>
        <li>Colour is emitted only for a TTY, so logs stay clean; <code style={s.code}>NO_COLOR</code> or{' '}
          <code style={s.code}>--no-color</code> forces it off.</li>
        <li>Posture is eventually consistent. <code style={s.code}>--fail-on-atrisk</code> waits for it and
          exits <code style={s.code}>3</code>; tune the wait with <code style={s.code}>--posture-timeout</code>.</li>
        <li>Full command surface and exit codes in{' '}
          <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-commands')}>Commands</button>.</li>
      </ul>
    </div>
  )
}
